import * as React from 'react';
import Link from 'components/Link';
import { useRouter } from 'next/router';
import { FaUser } from 'react-icons/fa';
import useViewer from 'hooks/useViewer';
import { BsArrowUpCircle } from 'react-icons/bs';
import SkipToContent from 'layouts/SkipToContent';
import { useCurrentUser } from 'hooks/useCurrentUser';
import Navbar from 'components/Navbar';
import PastDueBanner from 'features/dunning/PastDueBanner';
import { useMemberOffers } from 'context/MemberOffersContext';
import SaleBanner from 'components/MemberOffers/SaleBanner';
import MemberBannerPreview from 'components/MemberOffers/MemberBannerPreview';
import { readMemberBannerPreview } from 'components/MemberOffers/bannerPreview';
import useMemberBannerHeight from 'layouts/SidebarPage/useMemberBannerHeight';
import { getMemberOfferTeaser } from 'components/MemberOffers/memberOfferCopy';
import { getIsNativePlatform } from 'utils/client/mobile';
import {
  hasDashboardAccess,
  // hasAlertAccess,
} from 'services/client/token';
import { hasDashboardSubscription } from 'utils/client/dashboardPlan';
import Footer from 'layouts/Footer';
import SidebarLabel from 'components/SidebarLabel';
import {
  SETTINGS_PAGE,
  SIDEBAR_TOP_ITEMS,
  SIDEBAR_EDUCATION_ITEM,
  OPTION_FLOW_PAGE,
  PRICING_PAGE,
  SIGNUP_PAGE,
  HOME_PAGE,
  isSidebarItemActive,
} from 'constants/pages';
import { AuthStatus } from 'constants/auth';
import LogoSquare from 'svg/logo-square';
import { trackEvent } from 'services/client/analytics';
import { CLICK_UPGRADE } from 'services/client/analytics/events';
import classNames from 'styles/utils/classNames';
import { Props } from './props';

import { Page, PageContent, PageWrapper, Sidebar, SidebarItem, IconBackground } from './styles';

export default function PageLayoutDefault({
  children,
  hideSkipToContent,
  hideNav,
  subNav,
  hideFooter,
  isScreenHeight,
}: React.PropsWithChildren<Props>) {
  const router = useRouter();
  const viewer = useViewer();
  const native = getIsNativePlatform() || process.env.TARGET_PLATFORM === 'MOBILE';
  const preview = readMemberBannerPreview(router.query, process.env.APP_STAGE, native);
  // The old placement remains available only for the development/staging comparison fixture.
  const fitBanner = !native && preview?.layout !== 'current';
  const bannerSize = useMemberBannerHeight(fitBanner);
  const previewBanner = preview ? (
    <MemberBannerPreview key={router.asPath + JSON.stringify(preview)} config={preview} />
  ) : null;
  const isLoading = viewer.status === AuthStatus.Loading;
  const isDashboardUser = hasDashboardAccess(viewer);
  const currentUser = useCurrentUser();
  const isDashboardLifetime = currentUser?.user?.isDashboardLifetime;
  const offers = useMemberOffers();
  const memberOfferRow = offers.subscriptions[0];
  // The hover label names the pitch, and the arrow opens the plan tab that pitch is about.
  const memberOfferTeaser = memberOfferRow ? getMemberOfferTeaser(memberOfferRow) : null;
  const memberOfferTooltip = memberOfferTeaser?.tooltip || 'Member offers';
  const uid = viewer.status === AuthStatus.User ? viewer.viewer?.uid : undefined;
  // Someone signed in without a paid Dashboard plan gets the plans in the same modal instead.
  const opensPricing = !!uid && !getIsNativePlatform() && !hasDashboardSubscription(viewer);
  React.useEffect(() => {
    if (!uid || getIsNativePlatform()) return;
    void offers.refresh();
    const focus = () => void offers.refresh();
    window.addEventListener('focus', focus);
    return () => window.removeEventListener('focus', focus);
  }, [uid, offers.refresh]);
  // const isAlertUser = hasAlertAccess(viewer);

  return (
    <Page
      className="flex min-h-screen w-full min-w-full flex-col"
      data-member-banner-layout={fitBanner ? 'fit' : undefined}
    >
      {!hideSkipToContent && <SkipToContent />}
      {/*
        The past-due banner lives in the Navbar, and these app pages (flow, news, GEX, Settings…)
        hide it, so it is mounted here instead: above the sidebar and the page, in the normal flow.
        It adds its own height to the top of the page, so on a screen-height page the window
        scrolls that far; scrolling it out of view leaves the layout exactly as it was.
      */}
      {hideNav ? <PastDueBanner isStatic /> : <Navbar />}
      {!fitBanner && (preview ? previewBanner : <SaleBanner />)}
      {subNav}
      {/*
        The window scrolls the page. The sidebar sticks to the top of the screen beside it.
        Screen-height pages (the flow dashboards) are exactly one screen tall and scroll their
        own panels, so the window has nothing to scroll. Keep `overflow` off everything between
        the page and the window: an element that clips or scrolls (even sideways) becomes the
        box the sidebar sticks to, and the sidebar scrolls away with the page. On a short screen
        the sidebar's icons are taller than the sidebar; it clips them vertically only, so they
        can't lengthen the page and its hover labels still show beside it. Where `clip` isn't
        supported (Safari before 16), `hidden` is the fallback: the icons are still clipped, but
        the hover labels are hidden too. The footer is in the
        column beside the sidebar for the same reason: below the sidebar's row, it would push the
        sidebar off the screen.
      */}
      <PageWrapper className="flex max-w-full flex-grow">
        <Sidebar className="safearea-pad-top sticky top-0 hidden h-screen flex-shrink-0 flex-col items-center justify-between self-start overflow-y-hidden overflow-y-clip sm:flex">
          <div className="w-full">
            <Link href={isLoading || isDashboardUser ? OPTION_FLOW_PAGE : HOME_PAGE}>
              <SidebarItem className="mb-1 p-3">
                <LogoSquare />
              </SidebarItem>
            </Link>
            {SIDEBAR_TOP_ITEMS.map((item) => {
              if (!item) return null;

              const { href, Icon, name } = item;
              const isActive = isSidebarItemActive(item, router.pathname);

              return (
                <div className="mb-2" key={href}>
                  <Link key={href} href={href}>
                    <SidebarLabel name={name}>
                      <SidebarItem
                        key={href}
                        className="relative px-3 py-[.8rem]"
                        isActive={isActive}
                      >
                        <Icon className="h-full w-full fill-current" />
                      </SidebarItem>
                    </SidebarLabel>
                  </Link>
                </div>
              );
            })}
          </div>
          <div className="w-full">
            <div className="mb-2">
              <Link href={SIDEBAR_EDUCATION_ITEM.href}>
                <SidebarLabel name={SIDEBAR_EDUCATION_ITEM.name}>
                  <SidebarItem
                    className="relative px-3 py-[.8rem]"
                    isActive={isSidebarItemActive(SIDEBAR_EDUCATION_ITEM, router.pathname)}
                  >
                    <SIDEBAR_EDUCATION_ITEM.Icon className="h-full w-full stroke-current text-current" />
                  </SidebarItem>
                </SidebarLabel>
              </Link>
            </div>
            {offers.subscriptions.length > 0 && !getIsNativePlatform() && (
              <SidebarLabel name={memberOfferTooltip}>
                <button
                  type="button"
                  aria-label={
                    offers.campaign
                      ? `Member offers: ${offers.campaign.title}, ${memberOfferTooltip}`
                      : 'Member offers'
                  }
                  className="relative w-full"
                  onClick={() => {
                    offers.open(memberOfferRow?.id, undefined, 'nav', memberOfferTeaser?.term);
                    trackEvent(CLICK_UPGRADE, {
                      type: 'upgrade',
                      surface: 'member_offers',
                    });
                  }}
                >
                  <SidebarItem className="relative px-2.5 pb-3 pt-2">
                    <BsArrowUpCircle className="h-full w-full text-green-200" />
                    {(offers.unseen || offers.campaign) && (
                      <span
                        className="absolute right-1.5 top-1 flex h-2 w-2"
                        aria-label={offers.unseen ? 'Unseen offer' : undefined}
                        aria-hidden={offers.unseen ? undefined : true}
                      >
                        {offers.campaign && (
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75 motion-reduce:hidden" />
                        )}
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300 ring-2 ring-[#070a09]" />
                      </span>
                    )}
                    {offers.campaign && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded bg-emerald-300 px-1 text-[8px] font-semibold uppercase leading-3 text-[#06140d]">
                        Sale
                      </span>
                    )}
                  </SidebarItem>
                </button>
              </SidebarLabel>
            )}
            {!isDashboardLifetime && opensPricing && (
              <SidebarLabel name="Dashboard plans">
                <button
                  type="button"
                  aria-label="Dashboard plans"
                  className="relative w-full"
                  onClick={() => {
                    offers.open(undefined, undefined, 'nav');
                    trackEvent(CLICK_UPGRADE, {
                      type: 'pricing',
                      surface: 'member_offers',
                    });
                  }}
                >
                  <SidebarItem className="px-2.5 py-2">
                    <BsArrowUpCircle className="h-full w-full text-green-200" />
                  </SidebarItem>
                </button>
              </SidebarLabel>
            )}
            {!isDashboardLifetime &&
              !isDashboardUser &&
              !opensPricing &&
              offers.subscriptions.length === 0 && (
                <Link
                  href={PRICING_PAGE}
                  onClick={() =>
                    trackEvent(CLICK_UPGRADE, {
                      type: 'pricing',
                    })
                  }
                >
                  <SidebarItem className="px-2.5 py-2">
                    <BsArrowUpCircle className="h-full w-full text-green-200" />
                  </SidebarItem>
                </Link>
              )}
            <Link href={!!viewer?.viewer?.uid ? SETTINGS_PAGE : SIGNUP_PAGE}>
              <SidebarItem className="p-2 py-3">
                <IconBackground className="rounded-full p-2.5">
                  <FaUser className="h-full w-full fill-current" />
                </IconBackground>
              </SidebarItem>
            </Link>
          </div>
        </Sidebar>
        <div className="flex min-w-0 flex-grow flex-col">
          {fitBanner && (
            <div ref={bannerSize.ref} className="shrink-0">
              {preview ? previewBanner : <SaleBanner />}
            </div>
          )}
          <PageContent
            id="main"
            className={classNames(
              'block w-full min-w-0 flex-grow',
              // `vh`, like the dashboards' own panels. `clip` (with `hidden` as its fallback) can't
              // be scrolled at all, even by focus or scrollIntoView, so content below the screen
              // stays out of view.
              isScreenHeight && 'h-screen overflow-hidden overflow-clip',
            )}
            style={
              fitBanner
                ? ({
                    '--member-banner-height': `${bannerSize.height}px`,
                    ...(isScreenHeight && { height: `calc(100vh - ${bannerSize.height}px)` }),
                  } as React.CSSProperties)
                : undefined
            }
            tabIndex={-1}
          >
            {children}
          </PageContent>
          {!hideFooter && <Footer />}
        </div>
      </PageWrapper>
    </Page>
  );
}
